// Barra superior. En movil agrega un disparador para abrir el menu lateral
// como drawer y dejar todo el ancho al contenido principal.
import { useRef, useState } from "react";
import { Search, Upload, ArrowLeft, Building2, FolderOpen, LogOut, Loader, Menu, Home } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../api";
import NotificationBell from "./NotificationBell";

const ROLE_LABEL = { admin: "Administrador", empresa: "Empresa", auxiliar: "Auxiliar" };
const ROLE_COLOR = { admin: "#00A896", empresa: "#7C3AED", auxiliar: "#059669" };

function UserChip() {
  const { user, logout, updateUser } = useAuth();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handlePhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const updated = await api.uploadMyPhoto(fd);
      if (updated.error) throw new Error(updated.error);
      updateUser({ photo: updated.photo });
    } catch (e) {
      alert("No se pudo subir la foto: " + e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="user-chip">
      <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg" hidden onChange={(e) => handlePhoto(e.target.files[0])} />
      <div
        onClick={() => fileRef.current.click()}
        title="Cambiar foto de perfil"
        className="user-chip-avatar"
        style={{ background: user?.photo ? `url(${api.fileUrl(user.photo)}) center/cover` : (ROLE_COLOR[user?.role] || "#00A896") }}
      >
        {uploading ? <Loader size={13} className="spin" /> : (!user?.photo && user?.name?.charAt(0).toUpperCase())}
      </div>
      <div className="user-chip-meta">
        <p className="user-chip-name">{user?.name}</p>
        <p className="user-chip-role" style={{ color: ROLE_COLOR[user?.role] }}>{ROLE_LABEL[user?.role]}</p>
      </div>
      <NotificationBell />
      <button className="btn-icon user-chip-logout" onClick={logout} title="Cerrar sesion">
        <LogOut size={15} />
      </button>
    </div>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <div className="search-box">
      <Search size={16} className="search-icon" />
      <input type="text" className="search-input" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function MobileMenuButton({ onOpen }) {
  return (
    <button className="btn-icon mobile-menu-btn" onClick={onOpen} title="Abrir menu">
      <Menu size={18} />
    </button>
  );
}

function HomeButton({ onHome }) {
  if (!onHome) return null;
  return (
    <button className="btn-secondary" onClick={onHome} title="Ir a inicio">
      <Home size={15} /> Inicio
    </button>
  );
}

export default function Header({ search, onSearch, onUpload, company, folder, onBack, onHome, isTemplates, onOpenMobileNav }) {
  if (folder) {
    const contextLabel = company ? company.name : "Documentos Originales";
    return (
      <header className="topbar">
        <div className="topbar-left">
          <MobileMenuButton onOpen={onOpenMobileNav} />
          <div className="topbar-heading">
            <button className="btn-back" onClick={onBack}><ArrowLeft size={16} /> {contextLabel}</button>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div className="company-avatar" style={{ background: "#F5F3FF" }}>
                <FolderOpen size={18} style={{ color: "#7C3AED" }} />
              </div>
              <div>
                <h1 className="page-title">{folder}</h1>
                <p className="page-sub">{contextLabel}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="topbar-right">
          <HomeButton onHome={onHome} />
          <SearchBar value={search} onChange={onSearch} placeholder="Buscar documento..." />
          <button className="btn-primary" onClick={onUpload}><Upload size={16} /> Subir documento</button>
          <UserChip />
        </div>
      </header>
    );
  }

  if (company) {
    return (
      <header className="topbar">
        <div className="topbar-left">
          <MobileMenuButton onOpen={onOpenMobileNav} />
          <div className="topbar-heading">
            <button className="btn-back" onClick={onBack}><ArrowLeft size={16} /> Empresas</button>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div className="company-avatar"><Building2 size={18} /></div>
              <div>
                <h1 className="page-title">{company.name}</h1>
                <p className="page-sub">
                  {[company.nit && `NIT: ${company.nit}`, company.sector, company.city].filter(Boolean).join(" · ") || "Sin informacion adicional"}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="topbar-right">
          <HomeButton onHome={onHome} />
          <SearchBar value={search} onChange={onSearch} placeholder="Buscar carpeta..." />
          <UserChip />
        </div>
      </header>
    );
  }

  if (isTemplates) {
    return (
      <header className="topbar">
        <div className="topbar-left">
          <MobileMenuButton onOpen={onOpenMobileNav} />
          <div className="topbar-heading">
            <button className="btn-back" onClick={onBack}><ArrowLeft size={16} /> Empresas</button>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div className="company-avatar" style={{ background: "#F5F3FF" }}>
                <FolderOpen size={18} style={{ color: "#7C3AED" }} />
              </div>
              <div>
                <h1 className="page-title">Documentos Originales</h1>
                <p className="page-sub">Biblioteca base - se copian al crear cada empresa</p>
              </div>
            </div>
          </div>
        </div>
        <div className="topbar-right">
          <HomeButton onHome={onHome} />
          <SearchBar value={search} onChange={onSearch} placeholder="Buscar carpeta..." />
          <UserChip />
        </div>
      </header>
    );
  }

  return (
    <header className="topbar">
      <div className="topbar-left">
        <MobileMenuButton onOpen={onOpenMobileNav} />
        <div className="topbar-heading">
          <h1 className="page-title">Empresas</h1>
          <p className="page-sub">Gestion de clientes SST</p>
        </div>
      </div>
      <div className="topbar-right">
        <SearchBar value={search} onChange={onSearch} placeholder="Buscar empresa..." />
        <UserChip />
      </div>
    </header>
  );
}
